<script setup lang="ts">
// Branch protection for a repository (HRTH-116): which branches only some space roles may
// update or delete. The server keeps rules by blinded branch id, so it never learns branch
// names; this device matches them to the branches it knows.
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import type { RefProtection } from '../api/types'
import type { RefTarget } from '../lib/git'
import { useSession } from '../stores/session'

const props = defineProps<{
  library: string
  rules: RefProtection[]
  canManage: boolean
  /** Branch names by blinded id (the branches this device knows). */
  names: Map<string, string>
  branches: RefTarget[]
  /** Blinds a branch name under the repository's key. */
  blind: (name: string) => Promise<string>
}>()
const emit = defineEmits<{ changed: [] }>()
const session = useSession()

const open = ref(false)
const draft = ref<{ branch: string; roles: string[] } | null>(null)
const busy = ref(false)

/** Roles of the spaces this person is in (the server checks they're the repository's). */
const roles = computed(() =>
  session.spaces.filter((s) => !s.host).flatMap((s) => (s.roles ?? []).map((r) => ({ id: r.id, name: r.name, space: s.name }))),
)
const roleName = (id: string) => {
  const r = roles.value.find((x) => x.id === id)
  return r ? `${r.name} (${r.space})` : 'a role'
}
const short = (name: string) => name.replace(/^refs\/heads\//, '')

async function save(): Promise<void> {
  const d = draft.value
  if (!d || !d.branch || d.roles.length === 0) return
  busy.value = true
  try {
    await session.api.protectRef(props.library, await props.blind(d.branch), d.roles)
    draft.value = null
    emit('changed')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

async function remove(refId: string): Promise<void> {
  if (!confirm('Lift this protection?')) return
  try {
    await session.api.unprotectRef(props.library, refId)
    emit('changed')
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
</script>

<template>
  <div class="mb-3" data-testid="branch-protection">
    <button class="mono text-text-muted uppercase hover:text-text-primary" @click="open = !open">
      Branch protection · {{ String(rules.length).padStart(2, '0') }}
    </button>
    <div v-if="open" class="mt-2 border border-border p-3 text-sm">
      <p class="mb-2 text-text-muted">
        A protected branch only moves forward, and only holders of its roles (and their spaces' owners and admins) can
        update it. The server enforces this without knowing branch names.
      </p>
      <div v-for="r in rules" :key="r.ref_id" class="flex items-center gap-3 border-b border-border py-1.5">
        <span class="mono">🔒 {{ names.get(r.ref_id) ? short(names.get(r.ref_id)!) : 'a branch not pushed yet' }}</span>
        <span class="min-w-0 flex-1 truncate text-text-muted">{{ r.roles.map(roleName).join(', ') }}</span>
        <button v-if="canManage" class="mono text-text-muted hover:text-signal" @click="remove(r.ref_id)">✕</button>
      </div>
      <p v-if="rules.length === 0" class="mono text-text-muted">No protected branches.</p>
      <template v-if="canManage">
        <button v-if="!draft" class="btn-ghost mt-2 px-2" data-testid="protect-open" @click="draft = { branch: branches[0]?.name ?? '', roles: [] }">
          + Protect a branch
        </button>
        <form v-else class="mt-2 space-y-2" @submit.prevent="save">
          <select v-model="draft.branch" class="input w-auto" aria-label="Branch" data-testid="protect-branch">
            <option v-for="b in branches" :key="b.name" :value="b.name">{{ short(b.name) }}</option>
          </select>
          <div class="flex flex-wrap gap-3">
            <label v-for="r in roles" :key="r.id" class="inline-flex items-center gap-1.5">
              <input v-model="draft.roles" type="checkbox" :value="r.id" />
              {{ r.name }} <span class="text-text-muted">({{ r.space }})</span>
            </label>
            <span v-if="roles.length === 0" class="mono text-text-muted">Make a role in the space first (Space settings → Roles).</span>
          </div>
          <div class="flex justify-end gap-2">
            <button type="button" class="btn-ghost" @click="draft = null">Cancel</button>
            <button class="btn-primary" :disabled="busy || !draft.branch || draft.roles.length === 0" data-testid="protect-save">Protect</button>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>
